import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Sidebar as SidebarComponent } from './Sidebar';
import Powered from './Powered';
import HeaderLogo from './HeaderLogo';
import { useUIContext } from '@components/context/ApplicationContext';
import { debounce } from 'lodash';
import { useIsMounted } from '@/view/hooks/useIsMounted';
import { Navbar } from '@/view/components/Navbar';
import { BackgroundOrdersJobService } from '@/core/gql/apollo';
import { LocalStorage } from '@/core/Storage';

type MainLayoutProps = {} & React.HTMLAttributes<HTMLDivElement>;

const MainLayout: React.FC<MainLayoutProps> = props => {
  const {
    topPanel: { isTopPanelVisible },
    setRootContainer
  } = useUIContext();
  useAutoLogout();

  return (
    <Root className={props.className} ref={setRootContainer}>
      <Navbar />

      <Sidebar data-testid="Sidebar" data-main-layout-sidebar>
        <HeaderLogo />

        <SidebarComponent />

        <SidebarFooter>
          <Powered />
        </SidebarFooter>
      </Sidebar>

      <Main withTopPanel={isTopPanelVisible}>
        <Content className="Content">{props.children}</Content>
      </Main>
    </Root>
  );
};

const Sidebar = styled.div`
  display: flex;
  background-color: pink;
  flex-direction: column;
  justify-content: space-between;
  width: var(--sidebar-width, 88px);
  max-height: 100vh;
  z-index: 120;
  padding: 12px 8px;
  align-self: stretch;
  min-height: 100vh;
  position: relative;

  overflow: hidden;

  background-color: var(--white);
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.04);
  border: 1px solid var(--base-opacity-16, rgba(146, 151, 183, 0.16));

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const SidebarFooter = styled.div`
  display: grid;
`;

const Root = styled.div`
  display: flex;
  margin: 0;
  min-height: 100vh;
  flex-direction: row;
`;

const Main = styled.div<{ withTopPanel?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-top: ${props => (props.withTopPanel ? '48px' : '0')};

  background-color: var(--gray-1);

  transition: all 0.2s;
  @media screen and (max-width: 768px) {
    margin-left: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
`;

export default MainLayout;

const useAutoLogout = () => {
  const isMounted = useIsMounted();

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const startInterval = () => {
      timeout && clearTimeout(timeout);
      if (isMounted.current) {
        const ms = process.env.REACT_APP_LOGOUT_TIMEOUT;
        timeout = setTimeout(
          () => {
            removeListeners();
            const locationBeforeLogin = [window.location.pathname, window.location.search].filter(Boolean).join('');
            LocalStorage.clearAll();
            if (!locationBeforeLogin.includes('login')) {
              LocalStorage.setItem('location.beforeLogin', locationBeforeLogin);
            }
            BackgroundOrdersJobService.stopWatch();
          },
          ms === undefined ? 300000 : +ms
        );
      }
    };
    startInterval();
    const onActiveEvent = debounce(
      () => {
        startInterval();
      },
      1000,
      { maxWait: 2000 }
    );

    window.addEventListener('focus', onActiveEvent);
    window.addEventListener('keydown', onActiveEvent);
    window.addEventListener('mousemove', onActiveEvent);

    const removeListeners = () => {
      window.removeEventListener('focus', onActiveEvent);
      window.removeEventListener('keydown', onActiveEvent);
      window.removeEventListener('mousemove', onActiveEvent);
    };
    return () => {
      removeListeners();
      clearTimeout(timeout);
    };
  }, []);
};
